import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ExtractionRequest,
  ExtractionResponse,
  ExtractionProgressData,
  ConnectionStatus,
  SocketError,
} from "../types";
import { socketService } from "../services";
import { useToast } from "../components/ui/toast";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import {
  ExtractionForm,
  StatusPanel,
  LoginPage,
  UserProfile,
  UserPanel,
  AdminPanel,
} from "../components";
import Extractions from '../components/Extractions';
import { useMultiExtraction } from "../hooks/useMultiExtraction";
import { useExtractions } from "../hooks/useExtractions";
import { useTranslation } from "../i18n";
import ResultsContent from '../components/ResultsContent';
import { Badge } from "../components/ui/badge";
import { SimpleThemeToggle } from "../components/ui/theme-toggle";
import { useAuth } from "../contexts/AuthContext";

import { HiOutlineSparkles } from "react-icons/hi2";

const SoundToggle: React.FC = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = React.useState<boolean>(
    socketService.isSoundEnabled()
  );

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    socketService.setSoundEnabled(next);
    if (next) {
      socketService.playSound();
    }
  };

  return (
    <motion.button
      className={
        "relative p-2 rounded-lg border bg-background hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      }
      onClick={toggle}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title={enabled ? t('home.mute_notifications', 'Mute notifications') : t('home.enable_notifications', 'Enable notifications')}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={enabled ? "on" : "off"}
          initial={{ scale: 0, rotate: -90, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          exit={{ scale: 0, rotate: 90, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 15,
            duration: 0.3,
          }}
          className="w-5 h-5"
        >
          {enabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5 text-red-700" />
          )}
        </motion.div>
      </AnimatePresence>
    </motion.button>
  );
};

export const HomePage: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { t } = useTranslation();
  const { push } = useToast();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.DISCONNECTED
  );
  const [hoveredCodes, setHoveredCodes] = useState<string[] | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[] | null>(null);
  const [hideIrrelevant, setHideIrrelevant] = useState<boolean>(false);
  
  // Extractions hook for saved sessions
  const extractions = useExtractions();
  const {
    refreshSavedExtractions,
    waitForSavedExtractionAbortable,
    upsertSavedExtraction,
  } = extractions;
  const lastSavedErrorRef = useRef<string | null>(null);
  
  // Saved session selection (separate from active sessions)
  const [selectedSavedSessionId, setSelectedSavedSessionId] = useState<string | null>(null);
  
  // Multi-extraction state management
  const multiExtraction = useMultiExtraction();
  const { actions, selectors } = multiExtraction;
  const { getActiveSession, findByBackendRequestId, getAllSessions } = selectors;
  
  // Current session management
  const selectedSession = selectors.getSelectedSession();
  const activeSession = getActiveSession();
  const allSessions = getAllSessions();

  const resolveSessionForRequest = useCallback((requestId?: string | null) => {
    if (requestId) {
      const byBackend = findByBackendRequestId(requestId);
      if (byBackend) return byBackend;
      const bySessionId = getAllSessions().find(session => session.id === requestId);
      if (bySessionId) return bySessionId;
    }
    return getActiveSession();
  }, [findByBackendRequestId, getAllSessions, getActiveSession]);
  
  // Get selected saved session
  const selectedSavedSession = selectedSavedSessionId 
    ? extractions.savedSessions.find(s => s.id === selectedSavedSessionId) 
    : null;
  
  // Tab management
  const navigate = useNavigate();
  const location = useLocation();

  const parseTabFromLocation = () => {
    try {
      const params = new URLSearchParams(location.search);
      const t = params.get('tab');
      if (t === 'input' || t === 'extractions' || t === 'results' || t === 'admin' || t === 'profile') return t;
    } catch (_) {}
    return 'input';
  };

  const [activeTab, setActiveTab] = useState<"input" | "extractions" | "results" | "admin" | "profile">(parseTabFromLocation());
  const isAdmin = Boolean((user as any)?.is_admin);
  
  // Get the session to display in results/sidebar
  const displaySession = (() => {
    if (activeTab === "results") {
      const hasActiveResults = selectedSession?.originalText && selectedSession.results;
      const hasSavedResults = selectedSavedSession?.originalText && selectedSavedSession.results;
      return hasActiveResults ? selectedSession : hasSavedResults ? selectedSavedSession : activeSession;
    }
    return activeSession;
  })();
  
  // Track active extraction for auto-switching tabs
  const prevActiveSession = useRef<string | null>(null);
  useEffect(() => {
    const currentActiveId = activeSession?.id || null;
    
    // If a new extraction starts, auto-switch to extractions tab
    if (currentActiveId && currentActiveId !== prevActiveSession.current) {
      setActiveTab("extractions");
    }
    
    prevActiveSession.current = currentActiveId;
  }, [activeSession?.id]);

  // Keep URL in sync when activeTab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (activeTab) {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }
    // push new location without reloading
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: false });
  }, [activeTab]);

  // Listen to location changes (back/forward) and update activeTab
  useEffect(() => {
    const t = parseTabFromLocation();
    if (t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (activeTab === "admin" && !isAdmin) {
      setActiveTab("input");
    }
  }, [activeTab, isAdmin]);

  // Manage socket connection (only depends on auth state).
  // Avoid including rapidly changing dependencies that would cause reconnect loops.
  useEffect(() => {
    if (!isAuthenticated) {
      try { socketService.disconnect(); } catch (_) {}
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
      return;
    }
    let cancelled = false;
    (async () => {
      try { await socketService.connect(); } catch (err) { console.error("Failed to connect:", err); }
      if (!cancelled) {
        setConnectionStatus(socketService.getConnectionStatus());
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Subscribe to socket events; safe to depend on activeSession and actions since we DO NOT disconnect here.
  useEffect(() => {
    if (!isAuthenticated) return; // connection effect handles disconnect

    const unsubscribeStatus = socketService.onStatusChange(setConnectionStatus);
    const unsubscribeProgress = socketService.onProgress((data: ExtractionProgressData) => {
      const session = resolveSessionForRequest(data.request_id);
      const sessionId = session?.id;
      if (!sessionId) return;

      if (data.request_id) {
        actions.attachBackendRequestId(sessionId, data.request_id);
      }
      actions.updateProgress(sessionId, data);
    });
    const abortControllers: AbortController[] = [];
    const unsubscribeResult = socketService.onResult((data: ExtractionResponse) => {
      const session = resolveSessionForRequest(data.request_id);
      const sessionId = session?.id;
      if (!sessionId) return;

      if (data.request_id) {
        actions.attachBackendRequestId(sessionId, data.request_id);
      }

      actions.completeExtraction(sessionId, data);
      upsertSavedExtraction({ response: data, session });

      refreshSavedExtractions().catch(error => {
        console.error('Failed to refresh saved extractions after completion:', error);
      });

      if (data.request_id) {
        const ac = new AbortController();
        abortControllers.push(ac);
        void waitForSavedExtractionAbortable(data.request_id, { timeoutMs: 12000, intervalMs: 900, signal: ac.signal }).then(found => {
          if (!found && !ac.signal.aborted) {
            console.warn('Saved extraction did not appear within timeout for request_id', data.request_id);
            try {
              push({
                title: 'Sync timeout',
                description: 'Extraction saved record delayed. Use Refresh to try again.',
                variant: 'destructive',
                durationMs: 8000,
              });
            } catch (_) { /* ignore */ }
          }
        }).catch(err => {
          if (err?.message !== 'aborted') {
            console.warn('Polling error:', err);
          }
        });
      }
    });
    const unsubscribeError = socketService.onError((error: SocketError) => {
      if (error.code === 'AUTH_REQUIRED') {
        try { logout(); } catch (_) {}
        try {
          push({
            title: t('errors.auth_required', 'Authentication required'),
            description: error.message || t('errors.session_expired', 'Your session expired. Please sign in again.'),
            variant: 'destructive',
            durationMs: 8000,
          });
        } catch (_) { /* ignore */ }
        return;
      }
      const session = resolveSessionForRequest();
      if (session?.id) {
        actions.addError(session.id, error);
      }
      try {
        push({
          title: t('errors.title', 'Error'),
          description: error.message || t('errors.unknown', 'Unknown error'),
          variant: 'destructive',
          durationMs: 8000,
        });
      } catch (_) { /* ignore */ }
    });

    return () => {
      unsubscribeStatus();
      unsubscribeProgress();
      unsubscribeResult();
      abortControllers.forEach(c => c.abort());
      unsubscribeError();
    };
  }, [
    isAuthenticated,
    actions,
    resolveSessionForRequest,
    refreshSavedExtractions,
    waitForSavedExtractionAbortable,
    upsertSavedExtraction,
    logout,
    push,
    t,
  ]);

  useEffect(() => {
    const savedError = extractions.savedError;
    if (!savedError || savedError === lastSavedErrorRef.current) return;
    lastSavedErrorRef.current = savedError;
    try {
      push({
        title: t('errors.title', 'Error'),
        description: savedError,
        variant: 'destructive',
        durationMs: 8000,
      });
    } catch (_) { /* ignore */ }
  }, [extractions.savedError, push, t]);

  // Handle new extraction request
  const handleExtraction = useCallback(async (request: ExtractionRequest) => {
    try {
      // Start a new extraction session
      actions.startExtraction(request, request.text);
      
      // Make the socket request
      socketService.extractCodes(request);
      
      // Auto-switch to extractions tab
      setActiveTab("extractions");
    } catch (error) {
      console.error("Extraction failed:", error);
      
      // If we have an active session, add the error to it
      const sessionId = activeSession?.id;
      if (sessionId) {
        actions.addError(sessionId, {
          message: error instanceof Error ? error.message : "Unknown error occurred",
          code: "CLIENT_ERROR",
          timestamp: new Date().toISOString(),
        });
      }
    }
  }, [actions, activeSession?.id]);

  // Handle saved session selection (view results)
  const handleSavedSessionSelect = useCallback((sessionId: string | null) => {
    console.log("Selecting saved session:", sessionId);
    actions.selectSession(null); // Clear active session selection
    setSelectedSavedSessionId(sessionId);
    if (sessionId) {
      setActiveTab("results");
    }
  }, [actions]);

  // Assign colors when selected session changes
  useEffect(() => {
    if (!selectedSession?.results || !selectedSession.id) return;

    const results: any = selectedSession.results;
    const diagCodes: string[] = Array.isArray(results.codes)
      ? results.codes.map((c: any) => String(c?.code || '').trim()).filter(Boolean)
      : [];
    const pcsCodes: string[] = Array.isArray(results?.pcs?.final_codes)
      ? results.pcs.final_codes
          .flatMap((c: any) => [String(c?.code7 || '').trim(), String(c?.code4 || '').trim()])
          .filter(Boolean)
      : [];
    const allCodes = Array.from(new Set([...diagCodes, ...pcsCodes]));
    if (allCodes.length === 0) return;

    const existing = selectedSession.codeColors as Record<string, string> | undefined;
    const hasTailwindLike = (m?: Record<string, string>) => {
      if (!m) return false;
      return Object.values(m).some((v) => typeof v === 'string' && /(^|\s)(bg-|dark:|text-|ring-|from-|via-|to-)/.test(v));
    };

    // If we have no mapping yet, or the mapping uses Tailwind classes, regenerate as CSS colors
    // so the highlights can match the swatches exactly.
    if (!existing || Object.keys(existing).length === 0 || hasTailwindLike(existing)) {
      const hashToHue = (s: string) => {
        let hash = 0;
        for (let i = 0; i < s.length; i++) {
          hash = (hash << 5) - hash + s.charCodeAt(i);
          hash |= 0;
        }
        return Math.abs(hash) % 360;
      };

      const map: Record<string, string> = {};
      allCodes.forEach((code) => {
        const hue = hashToHue(code);
        const saturation = 65;
        const lightness = 72;
        map[code] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      });
      actions.updateCodeColors(selectedSession.id, map);
      return;
    }

    // Otherwise, fill in any missing codes without changing existing assignments.
    const next = { ...existing };
    let changed = false;
    const hashToHue = (s: string) => {
      let hash = 0;
      for (let i = 0; i < s.length; i++) {
        hash = (hash << 5) - hash + s.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash) % 360;
    };
    allCodes.forEach((code) => {
      if (!next[code]) {
        const hue = hashToHue(code);
        next[code] = `hsl(${hue}, 65%, 72%)`;
        changed = true;
      }
    });
    if (changed) actions.updateCodeColors(selectedSession.id, next);
  }, [selectedSession?.id, selectedSession?.results?.codes, selectedSession?.codeColors, actions]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background app-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {t("home.loading", "Loading...")}
          </p>
          {/* Sidebar - Status Panel */}
          <div className="lg:w-80">
            <StatusPanel
              connectionStatus={connectionStatus}
              session={displaySession}
              hoveredCodes={hoveredCodes}
              onCodeHover={setHoveredCodes}
              selectedCodes={selectedCodes}
              setSelectedCodes={setSelectedCodes}
              onRequestTabChange={(tab) => setActiveTab(tab)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Authentication required
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen bg-background app-bg flex flex-col">
      {/* Header */}
      <header className="border-b glass relative z-[200]">
        <div className="container flex h-16 items-center">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center">
              <img src="svgviewer-output.svg" alt="ICD Icon" className="h-12 w-12" />
            </div>
            <h1 className="text-xl font-semibold bg-gradient-to-r from-primary to-medical-600 bg-clip-text text-transparent flex items-center gap-1">
              {t("home.title", "ICD-10 Medical Code Extraction")}
              <HiOutlineSparkles className="w-5 h-5 fill-medical-600 stroke-medical-600" />
            </h1>
            <div className="flex items-center space-x-2">
              <div
                className={`h-2 w-2 rounded-full ${
                  connectionStatus === ConnectionStatus.CONNECTED 
                    ? "bg-success-600" 
                    : connectionStatus === ConnectionStatus.CONNECTING 
                      ? "bg-yellow-500" 
                      : "bg-destructive"
                }`}
              />
              <Badge
                variant={
                  connectionStatus === ConnectionStatus.CONNECTED
                    ? "success"
                    : connectionStatus === ConnectionStatus.CONNECTING
                      ? "secondary"
                      : "destructive"
                }
              >
                {connectionStatus === ConnectionStatus.CONNECTED
                  ? t("home.connected", "Connected")
                  : connectionStatus === ConnectionStatus.CONNECTING
                    ? t("home.connecting", "Connecting")
                    : t("home.disconnected", "Disconnected")}
              </Badge>
            </div>
          </div>
          <div className="ml-auto flex items-center space-x-4">
            <SimpleThemeToggle />
            <SoundToggle />
            <UserProfile />
          </div>
        </div>
        
        {/* Tabs Bar — moved into main container so content and tabs align */}
      </header>

      {/* Main Content */}
      <main className="container py-6 flex-1 w-full flex flex-col">
        {/* Tabs Bar (moved from header) */}
        <div className="mt-2 mb-4">
          <div className="inline-flex items-center gap-1 rounded-full border bg-muted/30 p-1">
            <button
              onClick={() => setActiveTab("input")}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                activeTab === "input" 
                  ? "bg-primary/10 text-primary border border-primary/30 shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("home.tab_input", "Input")}
            </button>
            <button
              onClick={() => setActiveTab("extractions")}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 relative ${
                activeTab === "extractions" 
                  ? "bg-primary/10 text-primary border border-primary/30 shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("home.tab_extractions", "Extractions")}
              {allSessions.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {allSessions.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("results")}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                activeTab === "results" 
                  ? "bg-primary/10 text-primary border border-primary/30 shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("home.tab_results", "Results")}
            </button>
          </div>
        </div>

  <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 items-start justify-center">
          {/* Primary Content Area */}
          <div className="flex-1">
            {activeTab === "input" && (
              <div className="flex justify-center">
                <div className="w-full">
                  <div className="space-y-6">
                    <ExtractionForm
                      onSubmit={handleExtraction}
                      isConnected={connectionStatus === ConnectionStatus.CONNECTED}
                      isExtracting={!!activeSession}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "extractions" && (
              <div className="space-y-6">
                <div className="mt-4">
                  <Extractions 
                    extractions={extractions}
                    onSelectSession={handleSavedSessionSelect}
                    selectedSessionId={selectedSavedSessionId}
                    activeSessions={selectors.getAllSessions()}
                  />
                </div>
              </div>
            )}

            {activeTab === "results" && (
              <ResultsContent
                session={displaySession}
                hideIrrelevant={hideIrrelevant}
                hoveredCodes={hoveredCodes}
                selectedCodes={selectedCodes}
                setHideIrrelevant={setHideIrrelevant}
                setHoveredCodes={setHoveredCodes}
                setSelectedCodes={setSelectedCodes}
              />
            )}
            {activeTab === "admin" && isAdmin && (
              <AdminPanel />
            )}
            {activeTab === "profile" && (
              <UserPanel />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
